import {
  addDoc,
  collection,
  deleteDoc,
  doc,
  getDocs,
  onSnapshot,
  query,
  serverTimestamp,
  updateDoc,
  where,
  writeBatch,
  type DocumentData,
  type Firestore,
  type Query,
  type QuerySnapshot,
} from 'firebase/firestore'
import type { Auth } from 'firebase/auth'
import type { ListItem, ListKind, ShoppingList } from '@domain/list'
import type { ListRepository, NewListItem } from '@domain/ports/list-repository'
import {
  booleanOr,
  optionalText,
  requireIso,
  requireOneOf,
  requireText,
  textOr,
  toIso,
} from './converters'
import { HOUSEHOLD_ID } from './household-repository'

/**
 * Listas e itens. **O único adapter do app que lê por assinatura.**
 *
 * ## `onSnapshot`, e não `getDocs` com um botão de atualizar
 *
 * *"É o lugar do app onde tempo real é requisito, não enfeite."* — e a definição
 * de pronto cobra: *"dois dispositivos veem a mesma lista mudar ao vivo, sem
 * refresh"*. Duas pessoas no mercado, cada uma com um celular; uma marca o
 * leite. Sem o tempo real, as duas compram leite.
 *
 * O resto do app continua lendo com `Promise`, e isso não é inconsistência: uma
 * transação de junho não muda enquanto alguém olha para ela, e um `onSnapshot`
 * ali seria uma conexão aberta por um dado parado.
 *
 * ## A escrita local aparece ANTES de o servidor confirmar, e é isso que se quer
 *
 * O SDK entrega o snapshot com a escrita otimista já aplicada
 * (`hasPendingWrites`), e só depois o confirmado. Este adapter **não filtra por
 * isso**: no corredor do mercado, com sinal ruim, o item tem de aparecer no
 * instante do Enter. Se a escrita falhar, o SDK a desfaz sozinho e o próximo
 * snapshot chega sem ela.
 *
 * ## `clearedAt` filtrado no servidor, ordenação em memória
 *
 * A consulta é uma igualdade só. `where('clearedAt','==',null) +
 * orderBy('position')` seriam dois campos e **exigiriam índice composto** — que
 * não falha no emulador, só em produção. A ordem sai de `sortItems`, no domínio,
 * que de todo jeito precisa agrupar os concluídos no fim: ordenar no servidor
 * daria uma ordem que a tela reordenaria em seguida.
 */

/** O truque de `comment-repository.ts`: o array em runtime cobre a união. */
type Equals<A, B> =
  (<T>() => T extends A ? 1 : 2) extends <T>() => T extends B ? 1 : 2 ? true : never

const LIST_KINDS = ['shopping', 'reminders'] as const
const _kindsCobremAUniao: Equals<ListKind, (typeof LIST_KINDS)[number]> = true
void _kindsCobremAUniao

function listsPath(): string {
  return `households/${HOUSEHOLD_ID}/lists`
}

function itemsPath(listId: string): string {
  return `${listsPath()}/${listId}/items`
}

function listaDe(id: string, data: DocumentData): ShoppingList {
  const onde = `lists/${id}`
  return {
    id,
    householdId: textOr(data['householdId'], HOUSEHOLD_ID),
    name: requireText(data['name'], `${onde}.name`),
    kind: requireOneOf(data['kind'], LIST_KINDS, `${onde}.kind`),
    icon: textOr(data['icon'], '📝'),
    // Desnormalizados pela Function. Zero enquanto ela não rodou — e a tela não
    // os usa para decidir nada, justamente porque eles chegam atrasados.
    itemCount: typeof data['itemCount'] === 'number' ? data['itemCount'] : 0,
    pendingCount: typeof data['pendingCount'] === 'number' ? data['pendingCount'] : 0,
    archivedAt: toIso(data['archivedAt']),
    createdAt: requireIso(data['createdAt'], `${onde}.createdAt`),
    updatedAt: requireIso(data['updatedAt'], `${onde}.updatedAt`),
  }
}

function itemDe(listId: string, id: string, data: DocumentData): ListItem {
  const onde = `lists/${listId}/items/${id}`
  return {
    id,
    householdId: textOr(data['householdId'], HOUSEHOLD_ID),
    listId,
    text: requireText(data['text'], `${onde}.text`),
    productId: optionalText(data['productId']),
    // Item criado antes do campo existir não tem a chave: `optionalText`
    // devolve `null`, que é o estado correto — nunca foi de um grupo.
    familyId: optionalText(data['familyId']),
    quantity: typeof data['quantity'] === 'number' ? data['quantity'] : null,
    unit: optionalText(data['unit']),
    note: optionalText(data['note']),
    completed: booleanOr(data['completed'], false),
    completedAt: toIso(data['completedAt']),
    completedBy: optionalText(data['completedBy']),
    dueAt: toIso(data['dueAt']),
    position: typeof data['position'] === 'number' ? data['position'] : 0,
    clearedAt: toIso(data['clearedAt']),
    createdBy: textOr(data['createdBy'], ''),
    // **`serverTimestamp()` chega `null` no snapshot local**, antes de o servidor
    // responder — e um `requireIso` aqui derrubaria a tela no instante do Enter,
    // que é exatamente o caso que o tempo real existe para servir. Daí o
    // fallback: a data certa chega no snapshot seguinte.
    createdAt: toIso(data['createdAt']) ?? '',
    updatedAt: toIso(data['updatedAt']) ?? '',
  }
}

/** Os itens vivos de uma lista. Ver o cabeçalho para por que só esta igualdade. */
function itensVivosQuery(db: Firestore, listId: string): Query {
  return query(collection(db, itemsPath(listId)), where('clearedAt', '==', null))
}

/** Registrado para a conferência de índices. Ver `firestore-indexes.test.ts`. */
export const LIST_QUERIES: Record<string, (db: Firestore, id: string) => Query> = {
  itensVivos: itensVivosQuery,
}

function camposDoItem(input: NewListItem): DocumentData {
  return {
    text: input.text,
    productId: input.productId,
    familyId: input.familyId,
    quantity: input.quantity,
    unit: input.unit,
    note: input.note,
    dueAt: input.dueAt === null ? null : new Date(input.dueAt),
    position: input.position,
  }
}

export function createFirebaseListRepository(db: Firestore, auth: Auth): ListRepository {
  function uid(): string {
    const atual = auth.currentUser?.uid
    if (atual === undefined) {
      throw new Error('listRepository: escrita sem sessão')
    }
    return atual
  }

  return {
    observeLists: (onChange, onError) =>
      onSnapshot(
        collection(db, listsPath()),
        (snap: QuerySnapshot) => {
          onChange(snap.docs.map((d) => listaDe(d.id, d.data())))
        },
        onError,
      ),

    observeItems: (listId, onChange, onError) =>
      onSnapshot(
        itensVivosQuery(db, listId),
        (snap: QuerySnapshot) => {
          onChange(snap.docs.map((d) => itemDe(listId, d.id, d.data())))
        },
        onError,
      ),

    createList: async (input) => {
      const criada = await addDoc(collection(db, listsPath()), {
        householdId: HOUSEHOLD_ID,
        name: input.name,
        kind: input.kind,
        icon: input.icon,
        archivedAt: null,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      })
      return criada.id
    },

    archiveList: async (listId) => {
      // As regras negam update que toque `itemCount`/`pendingCount`; estes dois
      // campos não são eles.
      await updateDoc(doc(db, listsPath(), listId), {
        archivedAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      })
    },

    addItem: async (listId, input) => {
      const criado = await addDoc(collection(db, itemsPath(listId)), {
        ...camposDoItem(input),
        householdId: HOUSEHOLD_ID,
        listId,
        completed: false,
        completedAt: null,
        completedBy: null,
        // Explícito, e **não** ausente: a consulta é `== null`, e no Firestore o
        // campo que não existe não casa com `== null`. Um item criado sem esta
        // linha nasceria invisível.
        clearedAt: null,
        createdBy: uid(),
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      })
      return criado.id
    },

    updateItem: async (listId, itemId, patch) => {
      const campos: DocumentData = { updatedAt: serverTimestamp() }
      if (patch.text !== undefined) campos['text'] = patch.text
      if (patch.productId !== undefined) campos['productId'] = patch.productId
      if (patch.familyId !== undefined) campos['familyId'] = patch.familyId
      if (patch.quantity !== undefined) campos['quantity'] = patch.quantity
      if (patch.unit !== undefined) campos['unit'] = patch.unit
      if (patch.note !== undefined) campos['note'] = patch.note
      if (patch.dueAt !== undefined) {
        campos['dueAt'] = patch.dueAt === null ? null : new Date(patch.dueAt)
      }
      if (patch.position !== undefined) campos['position'] = patch.position
      if (patch.completed !== undefined) {
        campos['completed'] = patch.completed
        // Carimbados aqui, e não pela tela. Ver a porta: "quem comprou" é o que
        // a outra pessoa quer saber, e deixá-lo à tela permitiria escrever o
        // nome errado.
        campos['completedAt'] = patch.completed ? serverTimestamp() : null
        campos['completedBy'] = patch.completed ? uid() : null
      }
      await updateDoc(doc(db, itemsPath(listId), itemId), campos)
    },

    moveItems: async (listId, positions) => {
      const lote = writeBatch(db)
      for (const { id, position } of positions) {
        lote.update(doc(db, itemsPath(listId), id), { position, updatedAt: serverTimestamp() })
      }
      await lote.commit()
    },

    removeItem: async (listId, itemId) => {
      await deleteDoc(doc(db, itemsPath(listId), itemId))
    },

    clearCompleted: async (listId) => {
      // Relê do servidor em vez de receber a lista da tela: entre o toque e o
      // commit, a outra pessoa pode ter marcado mais um — e limpar o que ela
      // acabou de marcar, sem ela ver, é pior do que deixar para a próxima.
      const vivos = await getDocs(itensVivosQuery(db, listId))
      const concluidos = vivos.docs.filter((d) => d.get('completed') === true)
      if (concluidos.length === 0) return

      const lote = writeBatch(db)
      for (const item of concluidos) {
        // Marca, **não apaga**: "ver o que já foi comprado é parte do valor".
        lote.update(item.ref, { clearedAt: serverTimestamp(), updatedAt: serverTimestamp() })
      }
      await lote.commit()
    },
  }
}
